import { useEffect, useRef } from 'react';
import { useTick } from '@pixi/react';
import { netService } from '../services/NetService';
import { hubService } from '../services/HubService';
import { HubPresenceHost } from '../net/hubPresenceHost';
import { HubPresenceClient } from '../net/hubPresenceClient';
import {
  HUB_PRESENCE_INTERVAL_MS,
  type HubPresenceMsg,
  type PresenceState,
} from '../net/hubPresence';
import { HUB_PLAYER_SPRITES } from '../data/hub.config';
import { useNetSessionStore } from '../store/netSessionSlice';

function localPresence(peerId: string): PresenceState {
  const speedSq = hubService.vx * hubService.vx + hubService.vy * hubService.vy;
  const moving =
    hubService.cinematicWalking ||
    speedSq > HUB_PLAYER_SPRITES.runThreshold * HUB_PLAYER_SPRITES.runThreshold;
  return {
    peerId,
    x: hubService.playerX,
    y: hubService.playerY,
    direction: hubService.facing,
    mapId: hubService.mapId,
    moving,
    status: 'active',
  };
}

/**
 * Drives the hub presence layer. Host: aggregates clients and broadcasts a
 * roster. Client: emits its own presence, follows the host's map, smooths the
 * roster. Offline: fully inert (no network path). Returns a getter for the
 * smoothed remote avatars, read each tick by HubScene.
 */
export function useHubPresence(): { getAvatars: () => PresenceState[] } {
  const hostRef = useRef<HubPresenceHost | null>(null);
  const clientRef = useRef<HubPresenceClient | null>(null);
  const avatarsRef = useRef<PresenceState[]>([]);
  const lastEmitRef = useRef(0);

  // The session is established from the in-hub station while this scene is
  // already mounted, so re-run whenever the session changes (epoch), not just
  // on mount — otherwise the controller is never wired at host/join time.
  const epoch = useNetSessionStore((s) => s.epoch);

  useEffect(() => {
    const session = netService.session;
    if (!session) {
      return;
    }
    const localId = session.localPeerId();
    if (netService.isHost()) {
      hostRef.current = new HubPresenceHost(session, () =>
        localPresence(localId),
      );
      return () => {
        hostRef.current?.dispose();
        hostRef.current = null;
      };
    }
    if (netService.isClient()) {
      const client = new HubPresenceClient((count) =>
        useNetSessionStore.getState().setPlayerCount(count),
      );
      clientRef.current = client;
      const off = session.on('hub-roster', (_from, m) => client.handle(m));
      return () => {
        off();
        clientRef.current = null;
      };
    }
    return undefined;
  }, [epoch]);

  useTick((delta) => {
    const session = netService.session;
    if (!session) {
      avatarsRef.current = [];
      return;
    }
    const localId = session.localPeerId();
    const now = performance.now();
    if (netService.isHost() && hostRef.current) {
      hostRef.current.broadcast(now);
      hostRef.current.view.update(
        hostRef.current.clientStates(),
        localId,
        delta,
      );
      avatarsRef.current = hostRef.current.view.avatars();
      return;
    }
    if (netService.isClient() && clientRef.current) {
      if (now - lastEmitRef.current >= HUB_PRESENCE_INTERVAL_MS) {
        lastEmitRef.current = now;
        const msg: HubPresenceMsg = {
          type: 'hub-presence',
          state: localPresence(localId),
        };
        session.broadcast('unreliable', msg);
      }
      const c = clientRef.current;
      if (c.hostMapId && c.hostMapId !== hubService.mapId) {
        void hubService.applyRemoteMap(c.hostMapId);
      }
      c.view.update(c.roster, localId, delta);
      avatarsRef.current = c.view.avatars();
      return;
    }
    avatarsRef.current = [];
  });

  return { getAvatars: () => avatarsRef.current };
}
